import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import AWS from 'aws-sdk';
import {
  trackTelemetryEvent,
  getUserGroupsFromIdToken,
  readTelemetryAuthContext,
  writeTelemetryAuthContext,
  clearTelemetryAuthContext,
  TelemetryAuthFlow,
  TelemetryAuthSource,
} from '../../utils/telemetry';

export type AuthStatus = 'SUCCESS' | 'NEW_PASSWORD_REQUIRED' | 'MFA_REQUIRED' | 'MFA_SETUP' | 'FAILURE';

interface AuthContextProps {
  user: IUserDetails | null;
  qrCode: string | null;
  login: (username: string, password: string) => Promise<AuthStatus>;
  loginWithSSO: () => void;
  handleOAuthCallback: (code: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<AuthStatus>;
  confirmMFA: (code: string) => Promise<boolean>;
  validateMFA: (code: string) => Promise<boolean>;
  logout: () => void;
  refresh: () => Promise<void>;
  reset: (username: string, oldPassword: string, newPassword: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
// --- Config ---
const poolData = {
  UserPoolId: process.env.REACT_APP_AWS_USER_POOL_ID || '',
  ClientId: process.env.REACT_APP_CLIENT_ID || '',
};

const userPool: CognitoUserPool | null =
  poolData.UserPoolId && poolData.ClientId ? new CognitoUserPool(poolData) : null;
const identityPoolId = process.env.REACT_APP_AWS_IDENTITY_POOL_ID || '';
const region = process.env.REACT_APP_AWS_REGION || 'us-east-2';
// Removed unused refreshIntervalSec (refresh now handled by fixed 60s interval below)
const autoLogoutDurationMs = parseInt(process.env.REACT_APP_AUTO_LOGOUT_DURATION_MIN || '30') * 60 * 1000;

let lastRefresh = 0;

// --- Helpers ---
const isTokenExpired = (idToken: string): boolean => {
  try {
    const [, payload] = idToken.split('.');
    const decoded = JSON.parse(atob(payload));
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const refresh = async (
  user: IUserDetails | null,
  logout: () => void,
  setUser: (user: IUserDetails | null) => void
): Promise<void> => {
  if (!user) return;
  if (!userPool) return;
  if (Date.now() - lastRefresh < 30000) return;
  lastRefresh = Date.now();

  const cognitoUser = new CognitoUser({ Username: user.email, Pool: userPool });
  const refreshToken = new CognitoRefreshToken({ RefreshToken: user.refreshToken });

  cognitoUser.refreshSession(refreshToken, (err, session) => {
    if (err) {
      console.error('Refresh token failed', err);
      logout();
    } else {
      handleSession(cognitoUser, session, logout, setUser);
    }
  });
};

// Track last applied idToken to know when to force identity clear (fresh auth vs refresh)
let lastAppliedIdToken: string | null = null;
// Global guard to suppress session application while a logout is in progress
let logoutInProgress = false;
// Track last credential error for optional UI surfacing (currently not exposed)
let lastCredentialError: any = null;

interface ResolvedIdentity {
  userSub: string;
  userEmail: string;
  userPrincipal: string;
}

const resolveIdentityFromPayload = (payload: any): ResolvedIdentity => {
  const userSub = String(payload?.sub || '').trim();
  const emailCandidate =
    payload?.email ||
    payload?.preferred_username ||
    payload?.upn ||
    payload?.['cognito:username'] ||
    '';
  const userEmail = String(emailCandidate || '').trim();
  const userPrincipal = String(
    payload?.preferred_username || payload?.upn || payload?.email || payload?.['cognito:username'] || userSub || ''
  ).trim();
  return {
    userSub,
    userEmail,
    userPrincipal,
  };
};

const buildInitials = (identifier: string): string => {
  const normalized = (identifier || '').trim();
  if (!normalized) return '';
  const localPart = normalized.includes('@') ? normalized.split('@')[0] : normalized;
  if (!localPart || localPart.length < 2) return localPart.slice(0, 1).toUpperCase();
  return `${localPart.slice(-1)}${localPart[0]}`.toUpperCase();
};

let sessionIdFallbackCounter = 0;

const generateSessionId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // no-op
  }
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      return `session-${Date.now()}-${hex}`;
    }
  } catch {
    // no-op
  }
  // Non-cryptographic fallback, only reached when Web Crypto is entirely unavailable.
  return `session-${Date.now()}-${(sessionIdFallbackCounter++).toString(16)}`;
};

const clearIdentityCache = () => {
  try {
    const existing = AWS.config.credentials as AWS.CognitoIdentityCredentials | undefined;
    existing?.clearCachedId?.();
  } catch (e) {
    console.warn('clearCachedId failed', e);
  }
  // Remove possible localStorage cached identity keys (pattern used by AWS SDK v2)
  try {
    if (typeof localStorage !== 'undefined') {
      const prefix = `aws.cognito.identity-id.${region}:${identityPoolId}`;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes(identityPoolId) && key.includes('identity-id')) {
          localStorage.removeItem(key);
        }
        if (key && key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
    }
  } catch (e) {
    console.warn('localStorage identity cache removal failed', e);
  }
};

const applyAwsCredentials = (
  idToken: string,
  forceClear: boolean,
  logout: () => void,
  onReady: (creds: AWS.CognitoIdentityCredentials) => void
) => {
  AWS.config.region = region;
  if (forceClear) {
    clearIdentityCache();
  }

  const makeCreds = () => new AWS.CognitoIdentityCredentials({
    IdentityPoolId: identityPoolId,
    Logins: {
      [`cognito-idp.${region}.amazonaws.com/${poolData.UserPoolId}`]: idToken,
    },
  });

  const attempt = (retry: boolean) => {
    if (logoutInProgress) return; // abort if logging out
    AWS.config.credentials = makeCreds();
    (AWS.config.credentials as AWS.CognitoIdentityCredentials).get((err) => {
      if (logoutInProgress) return; // ignore during logout
      if (err) {
        lastCredentialError = err;
        const code = (err as any)?.code;
        const msg = (err as any)?.message || '';
        if (!retry && code === 'NotAuthorizedException' && /Logins don't match/i.test(msg)) {
          console.warn('Credential mismatch (Logins dont match). Clearing identity cache and retrying once.');
          clearIdentityCache();
          return attempt(true);
        }
        console.error('AWS credential error', err);
        // Only force logout if not in a fresh auth finalize sequence where UI may retry manually.
        if (!retry && code === 'NotAuthorizedException') {
          // Allow UI to surface error instead of immediate logout; keep previous user until explicit action.
          return;
        }
        logout();
      } else {
        lastCredentialError = null;
        onReady(AWS.config.credentials as AWS.CognitoIdentityCredentials);
      }
    });
  };

  attempt(false);
};

const handleSession = (
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
  logout: () => void,
  setUser: (u: IUserDetails | null) => void,
  options?: {
    forceIdentityReset?: boolean;
    authFlow?: TelemetryAuthFlow;
    authSource?: TelemetryAuthSource;
    originAuthFlow?: TelemetryAuthFlow;
    emitLoginTelemetry?: boolean;
    emitSessionResumeTelemetry?: boolean;
    newSession?: boolean;
  }
) => {
  if (logoutInProgress) {
    console.warn('handleSession ignored: logout in progress');
    return;
  }
  const accessToken = session.getAccessToken().getJwtToken();
  const idToken = session.getIdToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();
  const payload = session.getIdToken().decodePayload();
  const identity = resolveIdentityFromPayload(payload);
  const forceClear = !!options?.forceIdentityReset || lastAppliedIdToken === null || lastAppliedIdToken !== idToken;
  applyAwsCredentials(idToken, forceClear, logout, (creds) => {
    if (logoutInProgress) {
      console.warn('applyAwsCredentials onReady ignored: logout in progress');
      return;
    }
    lastAppliedIdToken = idToken;

    const existingAuthContext = readTelemetryAuthContext();
    const resolvedAuthFlow: TelemetryAuthFlow =
      options?.authFlow ||
      existingAuthContext?.authFlow ||
      (options?.forceIdentityReset ? 'cognito_password' : 'cached_session');
    const resolvedAuthSource: TelemetryAuthSource =
      options?.authSource ||
      existingAuthContext?.authSource ||
      (options?.forceIdentityReset ? 'password' : 'cache_restore');
    const resolvedOriginAuthFlow: TelemetryAuthFlow =
      options?.originAuthFlow ||
      existingAuthContext?.originAuthFlow ||
      existingAuthContext?.authFlow ||
      resolvedAuthFlow;
    const resolvedSessionId =
      options?.newSession ? generateSessionId() : existingAuthContext?.sessionId || generateSessionId();

    writeTelemetryAuthContext({
      sessionId: resolvedSessionId,
      authFlow: resolvedAuthFlow,
      authSource: resolvedAuthSource,
      userSub: identity.userSub || null,
      userEmail: identity.userEmail || null,
      userPrincipal: identity.userPrincipal || null,
      originAuthFlow: resolvedOriginAuthFlow,
      establishedAt: new Date().toISOString(),
    });

    setUser({
      initials: buildInitials(identity.userEmail || identity.userPrincipal || identity.userSub),
      email: identity.userEmail,
      userId: identity.userSub,
      userPrincipal: identity.userPrincipal,
      authFlow: resolvedAuthFlow,
      authSource: resolvedAuthSource,
      sessionId: resolvedSessionId,
      accessToken,
      idToken,
      refreshToken,
      authenticatedAt: Date.now(),
      sessionToken: creds.sessionToken,
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
    });

    if (options?.emitLoginTelemetry) {
      trackTelemetryEvent({
        userSub: identity.userSub,
        userEmail: identity.userEmail || null,
        userGroups: getUserGroupsFromIdToken(idToken),
        authToken: idToken,
        actionType: 'login',
        actionTarget: 'auth/session',
        status: 'success',
        extra: {
          auth_flow: resolvedAuthFlow,
          auth_source: resolvedAuthSource,
          auth_origin_flow: resolvedOriginAuthFlow,
          user_principal: identity.userPrincipal || null,
          session_id: resolvedSessionId,
        },
      });
    }

    if (options?.emitSessionResumeTelemetry) {
      trackTelemetryEvent({
        userSub: identity.userSub,
        userEmail: identity.userEmail || null,
        userGroups: getUserGroupsFromIdToken(idToken),
        authToken: idToken,
        actionType: 'session_resume',
        actionTarget: 'auth/session',
        status: 'success',
        extra: {
          auth_flow: 'cached_session',
          auth_source: 'cache_restore',
          auth_origin_flow: resolvedOriginAuthFlow,
          user_principal: identity.userPrincipal || null,
          session_id: resolvedSessionId,
        },
      });
    }
  });
};

// --- Provider ---
let inactivityTimer: NodeJS.Timeout;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IUserDetails | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [challengeUser, setChallengeUser] = useState<CognitoUser | null>(null);
  const [challengeEmail, setChallengeEmail] = useState<string | null>(null);  // Preserve original login identifier (email) for labeling MFA QR codes before full session established.
  const [pendingCredentials, setPendingCredentials] = useState<{ username: string; password: string } | null>(null);
  const [initialLoginId, setInitialLoginId] = useState<string | null>(null); // original email/alias typed by user

  // Inactivity timer reset
  const resetInactivityTimer = () => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(() => {
      console.warn('Auto-logout due to inactivity');
      logout();
    }, autoLogoutDurationMs);
  };

  useEffect(() => {
    // Existing session restore for Cognito-native users
    // (OAuth callback is handled by CallbackView, not here)
    if (!userPool) return;
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (!err && session?.isValid()) {
          const existingAuthContext = readTelemetryAuthContext();
          handleSession(currentUser as CognitoUser, session as CognitoUserSession, logout, setUser, {
            authFlow: 'cached_session',
            authSource: 'cache_restore',
            originAuthFlow: existingAuthContext?.originAuthFlow || existingAuthContext?.authFlow || 'unknown',
            emitSessionResumeTelemetry: true,
            newSession: true,
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    // Refresh interval
    const interval = setInterval(() => {
      if (!user.idToken || isTokenExpired(user.idToken)) {
        refresh(user, logout, setUser);
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const handleFocus = () => {
      refresh(user, logout, setUser);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  // Inactivity logout handler
  useEffect(() => {
    if (!user) return;

    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];
    const handleActivity = () => resetInactivityTimer();

    resetInactivityTimer();
    events.forEach((event) => window.addEventListener(event, handleActivity));

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [user]);

  const loginWithSSO = () => {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN!;
    const clientId = process.env.REACT_APP_CLIENT_ID!;
    const callbackUrl = process.env.REACT_APP_CALLBACK_URL!;
    const url =
      `https://${domain}/oauth2/authorize` +
      `?client_id=${clientId}` +
      `&response_type=code` +
      `&scope=openid+email` +
      `&redirect_uri=${encodeURIComponent(callbackUrl)}` +
      `&identity_provider=EntraID`;
    window.location.href = url;
  };

  const buildUserFromTokens = (idToken: string, accessToken: string, refreshToken: string) => {
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const identity = resolveIdentityFromPayload(payload);
      const existingAuthContext = readTelemetryAuthContext();
      const sessionId = generateSessionId();
      applyAwsCredentials(idToken, true, logout, (creds) => {
        writeTelemetryAuthContext({
          sessionId,
          authFlow: 'cognito_oauth',
          authSource: 'sso',
          userSub: identity.userSub || null,
          userEmail: identity.userEmail || null,
          userPrincipal: identity.userPrincipal || null,
          originAuthFlow: existingAuthContext?.originAuthFlow || 'cognito_oauth',
          establishedAt: new Date().toISOString(),
        });

        setUser({
          email: identity.userEmail,
          userId: identity.userSub,
          userPrincipal: identity.userPrincipal,
          authFlow: 'cognito_oauth',
          authSource: 'sso',
          sessionId,
          accessToken,
          idToken,
          refreshToken,
          authenticatedAt: Date.now(),
          initials: buildInitials(identity.userEmail || identity.userPrincipal || identity.userSub),
          sessionToken: creds.sessionToken,
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
        });
        trackTelemetryEvent({
          userSub: identity.userSub,
          userEmail: identity.userEmail || null,
          userGroups: getUserGroupsFromIdToken(idToken),
          authToken: idToken,
          actionType: 'login',
          actionTarget: 'auth/session',
          status: 'success',
          extra: {
            auth_flow: 'cognito_oauth',
            auth_source: 'sso',
            auth_origin_flow: 'cognito_oauth',
            user_principal: identity.userPrincipal || null,
            session_id: sessionId,
          },
        });
      });
    } catch (e) {
      console.error('buildUserFromTokens :: failed to decode idToken', e);
    }
  };

  const handleOAuthCallback = async (code: string) => {
    try {
      const domain = process.env.REACT_APP_COGNITO_DOMAIN!;
      const clientId = process.env.REACT_APP_CLIENT_ID!;
      const callbackUrl = process.env.REACT_APP_CALLBACK_URL!;
      const response = await fetch(`https://${domain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          redirect_uri: callbackUrl,
        }),
      });
      const tokens = await response.json();
      if (tokens.id_token) {
        buildUserFromTokens(tokens.id_token, tokens.access_token, tokens.refresh_token);
      } else {
        console.error('handleOAuthCallback :: token exchange failed', tokens);
        trackTelemetryEvent({
          userSub: null,
          userEmail: null,
          userGroups: [],
          actionType: 'login',
          actionTarget: 'auth/session',
          status: 'failure',
          extra: {
            auth_flow: 'cognito_oauth',
            auth_source: 'sso',
            error: 'token_exchange_failed',
          },
        });
      }
    } catch (e) {
      console.error('handleOAuthCallback :: error', e);
      trackTelemetryEvent({
        userSub: null,
        userEmail: null,
        userGroups: [],
        actionType: 'login',
        actionTarget: 'auth/session',
        status: 'failure',
        extra: {
          auth_flow: 'cognito_oauth',
          auth_source: 'sso',
          error: String((e as any)?.message || e),
        },
      });
    }
  };

  const login = async (username: string, password: string): Promise<AuthStatus> => {
    if (!userPool) {
      setChallengeUser(null);
      return 'FAILURE';
    }

    setQrCode(null);
    setChallengeUser(null);
    setChallengeEmail(username); // assume login uses email as username
    setPendingCredentials({ username, password });
    setInitialLoginId(username);

    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: username, Password: password });

    return new Promise((resolve) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          // Fresh login -> force identity reset
          handleSession(cognitoUser, session, logout, setUser, {
            forceIdentityReset: true,
            authFlow: 'cognito_password',
            authSource: 'password',
            originAuthFlow: 'cognito_password',
            emitLoginTelemetry: true,
            newSession: true,
          });
          setChallengeEmail(null);
          setPendingCredentials(null);
          resolve('SUCCESS');
        },
        onFailure: (err) => {
          console.error('Login failed', err);
          trackTelemetryEvent({
            userSub: null,
            userEmail: username,
            userGroups: [],
            actionType: 'login',
            actionTarget: 'auth/session',
            status: 'failure',
            extra: {
              auth_flow: 'cognito_password',
              auth_source: 'password',
              user_principal: username,
              error: String((err as any)?.message || err),
            },
          });
          setPendingCredentials(null); // clear retained password on failure
          resolve('FAILURE');
        },
        newPasswordRequired: (userAttributes) => {
          // Capture email attribute if available
          const emailAttr = (userAttributes as any)?.email || (userAttributes as any)?.Email;
          if (emailAttr) setChallengeEmail(emailAttr);
          setChallengeUser(cognitoUser);
          resolve('NEW_PASSWORD_REQUIRED');
        },
        mfaSetup: () => {
          cognitoUser.associateSoftwareToken({
            onFailure: (err) => {
              console.error('MFA setup error', err);
              resolve('FAILURE');
            },
            associateSecretCode: (secretCode) => {
              const issuer = 'hcrc-web';
              const account = challengeEmail || username;
              const label = encodeURIComponent(`${issuer}:${account}`);
              const otpauth = `otpauth://totp/${label}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
              setQrCode(otpauth);
              setChallengeUser(cognitoUser);
              resolve('MFA_SETUP');
            },
          });
        },
        totpRequired: () => {
          setChallengeUser(cognitoUser);
          resolve('MFA_REQUIRED');
        },
      });
    });
  };

  const reset = async (username: string, oldPassword: string, newPassword: string): Promise<boolean> => {
    if (!userPool) return false;

    setQrCode(null);
    setChallengeUser(null);
    setChallengeEmail(username);
    setInitialLoginId(username);
    setPendingCredentials({ username, password: oldPassword });

    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: username, Password: oldPassword });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          // Password was already permanent; treat as a successful login (not a reset flow).
          handleSession(cognitoUser, session, logout, setUser, {
            forceIdentityReset: true,
            authFlow: 'cognito_password',
            authSource: 'password',
            originAuthFlow: 'cognito_password',
            emitLoginTelemetry: true,
            newSession: true,
          });
          resolve(true);
        },
        onFailure: (err) => {
          console.error('Reset password failed:', err);
          setPendingCredentials(null);
          resolve(false);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          const emailAttr = (userAttributes as any)?.email || (userAttributes as any)?.Email;
          if (emailAttr) setChallengeEmail(emailAttr);
          setChallengeUser(cognitoUser);
          cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
            onSuccess: (session) => {
              // If Cognito finalizes here (no MFA required afterwards)
              handleSession(cognitoUser, session, logout, setUser, {
                forceIdentityReset: true,
                authFlow: 'cognito_password',
                authSource: 'password',
                originAuthFlow: 'cognito_password',
                emitLoginTelemetry: true,
                newSession: true,
              });
              setChallengeUser(null);
              setChallengeEmail(null);
              setPendingCredentials({ username: initialLoginId || username, password: newPassword });
              resolve(true);
            },
            onFailure: (err) => {
              console.error('New password challenge (reset) failed:', err);
              resolve(false);
            },
          });
        },
        mfaSetup: () => {
          // Immediately after new password, Cognito requests MFA setup.
          cognitoUser.associateSoftwareToken({
            onFailure: (err) => {
              console.error('MFA setup error during reset', err);
              resolve(false);
            },
            associateSecretCode: (secretCode) => {
              const issuer = 'hcrc-web';
              const account = challengeEmail || username;
              const label = encodeURIComponent(`${issuer}:${account}`);
              const otpauth = `otpauth://totp/${label}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
              setQrCode(otpauth);
              setChallengeUser(cognitoUser);
              // Not complete yet; caller should navigate to MFA setup UI.
              resolve(false);
            },
          });
        },
        totpRequired: () => {
          // TOTP verification required (user already has MFA configured)
          setChallengeUser(cognitoUser);
          resolve(false);
        },
      });
    });
  };

  const validateMFA = async (code: string): Promise<boolean> => {
    if (!userPool) return false;

    return new Promise((resolve) => {
      if (!challengeUser) return resolve(false);
      challengeUser.verifySoftwareToken(code, 'device', {
        onSuccess: () => {
          // Attempt to mark TOTP as preferred & immediately finalize session so user doesn't need to re-login.
          const finalize = () => {
            const creds = pendingCredentials;
            // Clear QR & challenge user (setup phase done).
            // Try to hydrate session directly from existing challengeUser before fallback re-auth.
            challengeUser.getSession((sessionErr: any, session: CognitoUserSession | null) => {
              if (!sessionErr && session?.isValid()) {
                // We have a valid session already; use it.
                handleSession(challengeUser, session, logout, setUser, {
                  forceIdentityReset: true,
                  authFlow: 'cognito_password',
                  authSource: 'password',
                  originAuthFlow: 'cognito_password',
                  emitLoginTelemetry: true,
                  newSession: true,
                });
                setQrCode(null);
                setChallengeUser(null);
                setChallengeEmail(null);
                setPendingCredentials(null);
                setInitialLoginId(null);
                resolve(true);
              } else {
                // Fallback: perform explicit re-auth using stored credentials.
                setQrCode(null);
                if (creds) {
                  const reauthUser = new CognitoUser({ Username: creds.username, Pool: userPool });
                  const authDetails = new AuthenticationDetails({ Username: creds.username, Password: creds.password });
                  reauthUser.authenticateUser(authDetails, {
                    onSuccess: (reauthSession) => {
                      handleSession(reauthUser, reauthSession, logout, setUser, {
                        forceIdentityReset: true,
                        authFlow: 'cognito_password',
                        authSource: 'password',
                        originAuthFlow: 'cognito_password',
                        emitLoginTelemetry: true,
                        newSession: true,
                      });
                      setChallengeUser(null);
                      setChallengeEmail(null);
                      setPendingCredentials(null);
                      setInitialLoginId(null);
                      resolve(true);
                    },
                    onFailure: (err) => {
                      console.error('Auto re-auth after MFA setup failed', err);
                      setChallengeUser(null); // token verified; require manual login
                      resolve(true);
                    },
                    totpRequired: () => {
                      // Need user to enter a code for first post-setup auth.
                      setChallengeUser(reauthUser);
                      resolve(true);
                    },
                  });
                } else {
                  console.warn('No pending credentials stored; user must login manually after MFA setup.');
                  setChallengeUser(null);
                  resolve(true);
                }
              }
            });
          };

          const anyUser: any = challengeUser as any;
          if (typeof anyUser.setUserMfaPreference === 'function') {
            try {
              // AWS SDK expects (smsMfaSettings, softwareTokenMfaSettings, callback)
              anyUser.setUserMfaPreference(
                { PreferredMfa: false, Enabled: false }, // sms (disabled)
                { PreferredMfa: true, Enabled: true },   // software token (preferred)
                (prefErr: any) => {
                  if (prefErr) console.warn('setUserMfaPreference failed (continuing anyway)', prefErr);
                  finalize();
                }
              );
            } catch (e) {
              console.warn('setUserMfaPreference threw (continuing anyway)', e);
              finalize();
            }
          } else {
            finalize();
          }
        },
        onFailure: (err) => {
          console.error('MFA verification failed', err);
          setPendingCredentials(null); // don't keep credentials after failure
          resolve(false);
        },
      });
    });
  };

  const completeNewPassword = async (newPassword: string): Promise<AuthStatus> => {
    return new Promise((resolve) => {
      if (!challengeUser) return resolve('FAILURE');
      const identifier = initialLoginId || challengeEmail || challengeUser.getUsername();
      setPendingCredentials({ username: identifier, password: newPassword });
      challengeUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          // Flow finalized with no further MFA steps.
          handleSession(challengeUser, session, logout, setUser, {
            forceIdentityReset: true,
            authFlow: 'cognito_password',
            authSource: 'password',
            originAuthFlow: 'cognito_password',
            emitLoginTelemetry: true,
            newSession: true,
          });
          setChallengeUser(null);
          setChallengeEmail(null);
          setPendingCredentials(null);
          setInitialLoginId(null);
          resolve('SUCCESS');
        },
        onFailure: (err: any) => {
          if (err?.code === 'NotAuthorizedException' && /Invalid session/i.test(err?.message || '')) {
            console.error('New password challenge failed: invalid/expired session; prompt user to restart sign-in.', err);
          } else {
            console.error('New password challenge failed', err);
          }
          setPendingCredentials(null);
          resolve('FAILURE');
        },
        mfaSetup: () => {
          // After new password, Cognito requests MFA setup.
          challengeUser.associateSoftwareToken({
            onFailure: (err) => {
              console.error('MFA setup error (post-new-password)', err);
              resolve('FAILURE');
            },
            associateSecretCode: (secretCode) => {
              const issuer = 'hcrc-web';
              // Prefer captured email (original login identifier) else fallback to Cognito username.
              const account = challengeEmail || initialLoginId || challengeUser.getUsername();
              const label = encodeURIComponent(`${issuer}:${account}`);
              const otpauth = `otpauth://totp/${label}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
              setQrCode(otpauth);
              resolve('MFA_SETUP');
              // challengeUser retained for subsequent MFA validation.
            },
          });
        },
        totpRequired: () => {
          // User already has TOTP configured; they must enter a code.
          resolve('MFA_REQUIRED');
        },
        mfaRequired: (challengeName: string, challengeParameters: any) => {
          console.warn('MFA required after new password challenge', { challengeName, challengeParameters });
          resolve('MFA_REQUIRED');
        },
      });
    });
  };

  const confirmMFA = async (code: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!challengeUser) return resolve(false);
      challengeUser.sendMFACode(code, {
        onSuccess: (session) => {
          handleSession(challengeUser, session, logout, setUser, {
            forceIdentityReset: true,
            authFlow: 'cognito_password',
            authSource: 'password',
            originAuthFlow: 'cognito_password',
            emitLoginTelemetry: true,
            newSession: true,
          });
          setChallengeUser(null);
          setChallengeEmail(null);
          setPendingCredentials(null);
          setInitialLoginId(null);
          resolve(true);
        },
        onFailure: (err) => {
          console.error('MFA failed', err);
          setPendingCredentials(null);
          resolve(false);
        },
      }, 'SOFTWARE_TOKEN_MFA');
    });
  };

  const logout = () => {
    const userBeforeLogout = user;
    const currentAuthContext = readTelemetryAuthContext();
    if (inactivityTimer) clearTimeout(inactivityTimer);
    logoutInProgress = true;
    try {
      userPool?.getCurrentUser()?.signOut();
    } catch (e) {
      console.warn('signOut threw (continuing)', e);
    }
    // Clear AWS credential identity explicitly
    try {
      const existing = AWS.config.credentials as AWS.CognitoIdentityCredentials | undefined;
      existing?.clearCachedId?.();
    } catch (e) {
      console.warn('Failed clearing cached identity on logout', e);
    }
    AWS.config.credentials = undefined as any;
    lastAppliedIdToken = null; // reset identity tracking on full logout
    setUser(null);
    setQrCode(null);
    setChallengeUser(null);
    setChallengeEmail(null);
    setPendingCredentials(null);
    setInitialLoginId(null);
    if (userBeforeLogout) {
      trackTelemetryEvent({
        userSub: userBeforeLogout.userId,
        userEmail: userBeforeLogout.email,
        userGroups: getUserGroupsFromIdToken(userBeforeLogout.idToken),
        authToken: userBeforeLogout.idToken,
        actionType: 'logout',
        actionTarget: 'auth/session',
        status: 'success',
        extra: {
          auth_flow: userBeforeLogout.authFlow || currentAuthContext?.authFlow || 'unknown',
          auth_source: userBeforeLogout.authSource || currentAuthContext?.authSource || 'unknown',
          auth_origin_flow: currentAuthContext?.originAuthFlow || currentAuthContext?.authFlow || 'unknown',
          user_principal: userBeforeLogout.userPrincipal || currentAuthContext?.userPrincipal || null,
          session_id: userBeforeLogout.sessionId || currentAuthContext?.sessionId || null,
        },
      });
    }
    clearTelemetryAuthContext();
    // Allow future logins after short defer (ensures pending async callbacks are skipped)
    setTimeout(() => { logoutInProgress = false; }, 750);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        qrCode,
        login,
        loginWithSSO,
        handleOAuthCallback,
        logout,
        refresh: () => refresh(user, logout, setUser),
        completeNewPassword,
        confirmMFA,
        validateMFA,
        reset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// --- Hook ---
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};